import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  CardBody,
  Alert,
  FormFeedback,
  Col,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import axios from 'axios';
import swal from 'sweetalert';
import Loader from "../../../components/Loader";
import logo from "../../../assets/images/gov-logo.png";
import backend_url from "../../../routes/API_URL";

const API_URL = backend_url['api_url'];

const PasswordResetModal = () => {
  const history = useHistory();
  
  // Get data from sessionStorage
  const full_name = `${sessionStorage.getItem("sal") || ''} ${sessionStorage.getItem("fname") || ''} ${sessionStorage.getItem("lname") || ''}`.trim();
  
  const [modalVisible, setModalVisible] = useState(true);
  const [formData, setFormData] = useState({
    user_password: '',
    user_password_confirm: '',
    email_id: sessionStorage.getItem("umail") || '',
    id: sessionStorage.getItem("uid") || '',
    bearer_auth: sessionStorage.getItem('token') || ''
  });
  
  const [validation, setValidation] = useState({
    passwordValid: false,
    passwordMatch: false,
    passwordTouched: false,
    confirmTouched: false,
    UpwordState: null,
    UpwordConfirmState: null
  });
  
  const [uiState, setUiState] = useState({
    is_loading: false,
    showPassword: false,
    showConfirmPassword: false
  });
  
  // Password strength checker
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  // Validate password in real-time
  useEffect(() => {
    if (formData.user_password) {
      const checks = {
        hasMinLength: formData.user_password.length >= 8,
        hasUppercase: /[A-Z]/.test(formData.user_password),
        hasLowercase: /[a-z]/.test(formData.user_password),
        hasNumber: /[0-9]/.test(formData.user_password),
        hasSpecialChar: /[#.,;~%?!@$%^&*-]/.test(formData.user_password)
      };
      
      const score = Object.values(checks).filter(Boolean).length;
      const isValid = score >= 4 && checks.hasMinLength;
      
      setPasswordStrength({
        ...checks,
        score
      });
      
      setValidation(prev => ({
        ...prev,
        passwordValid: isValid,
        UpwordState: isValid ? 'has-success' : 'has-danger'
      }));
    } else {
      setPasswordStrength({
        score: 0,
        hasMinLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSpecialChar: false
      });
      setValidation(prev => ({
        ...prev,
        passwordValid: false,
        UpwordState: null
      }));
    }
  }, [formData.user_password]);

  // Validate password match in real-time
  useEffect(() => {
    if (formData.user_password_confirm) {
      const match = formData.user_password === formData.user_password_confirm;
      setValidation(prev => ({
        ...prev,
        passwordMatch: match,
        UpwordConfirmState: match ? 'has-success' : 'has-danger'
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        passwordMatch: false,
        UpwordConfirmState: null
      }));
    }
  }, [formData.user_password, formData.user_password_confirm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark field as touched
    if (name === 'user_password') {
      setValidation(prev => ({ ...prev, passwordTouched: true }));
    } else if (name === 'user_password_confirm') {
      setValidation(prev => ({ ...prev, confirmTouched: true }));
    }
  };

  const togglePasswordVisibility = (field) => {
    if (field === 'password') {
      setUiState(prev => ({ ...prev, showPassword: !prev.showPassword }));
    } else {
      setUiState(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }));
    }
  };

  const toggleModal = () => {
    setModalVisible(!modalVisible);
  };

  const validateForm = () => {
    if (!formData.user_password) {
      return false;
    }
    
    if (!validation.passwordValid) {
      return false;
    }
    
    if (!formData.user_password_confirm) {
      return false;
    }
    
    if (!validation.passwordMatch) {
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setUiState(prev => ({ ...prev, is_loading: true }));
    
    try {
      const payload = {
        email_id: formData.email_id,
        user_password: formData.user_password,
      };
      
      const response = await axios.patch(
        `${API_URL}/users/reset_password/${formData.id}`, 
        payload,
        { 
          headers: { 
            "Authorization": `Bearer ${formData.bearer_auth}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );
      
      setUiState(prev => ({ ...prev, is_loading: false }));
      setModalVisible(false);
      
      await swal({
        title: "Success!",
        text: "Password updated successfully! For this reason we're logging you out. Use the password for next log in.",
        icon: "success",
        button: "OK",
      });
      
      // Clear session and redirect to login
      sessionStorage.clear();
      window.location.href = '/login';
      
    } catch (error) {
      setUiState(prev => ({ ...prev, is_loading: false }));
      setModalVisible(false);
      
      let errorMessage = '';
      
      if (error.response) {
        if (error.response.status === 404) {
          errorMessage = 'User not found. Please try again.';
        } else if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Your session may have expired.';
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = 'Failed to update password.';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection.';
      } else {
        errorMessage = error.message;
      }
      
      await swal({
        title: "Error",
        text: errorMessage + " Contact the system administrator for more.",
        icon: "error",
        button: "OK",
      });
      
      setModalVisible(true);
    }
  };

  // Get password strength color
  const getStrengthColor = () => {
    if (passwordStrength.score === 0) return 'secondary';
    if (passwordStrength.score <= 2) return 'danger';
    if (passwordStrength.score <= 3) return 'warning';
    return 'success';
  };

  // Get password strength label
  const getStrengthLabel = () => {
    if (passwordStrength.score === 0) return 'No password';
    if (passwordStrength.score <= 2) return 'Weak';
    if (passwordStrength.score <= 3) return 'Fair';
    if (passwordStrength.score === 4) return 'Good';
    return 'Strong';
  };

  return (
    <Modal isOpen={modalVisible} size="lg" className="password-reset-modal">
      <Form onSubmit={handleSubmit}>
        <ModalHeader toggle={toggleModal} className="bg-danger text-white">
          <center className="w-100">
            <strong>PASSWORD RESET FORM FOR {full_name.toUpperCase()}</strong>
          </center>
        </ModalHeader>
        
        <ModalBody>
          {uiState.is_loading && <Loader />}
          
          <FormGroup>
            <Row>
              <Col xs={12} md={3}>
                <Label for="email_id" className="font-weight-bold">Email Address</Label>
              </Col>
              <Col xs={12} md={9}>
                <Input
                  type="email"
                  id="email_id"
                  name="email_id"
                  value={formData.email_id}
                  readOnly
                  className="bg-light"
                />
                <small className="text-muted">
                  This is the email associated with your account
                </small>
              </Col>
            </Row>
          </FormGroup>
          
          {/* New Password */}
          <FormGroup>
            <Row>
              <Col xs={12} md={3}>
                <Label for="user_password" className="font-weight-bold">New Password</Label>
              </Col>
              <Col xs={12} md={9}>
                <div className="d-flex">
                  <Input
                    type={uiState.showPassword ? "text" : "password"}
                    id="user_password"
                    name="user_password"
                    value={formData.user_password}
                    onChange={handleInputChange}
                    invalid={validation.UpwordState === 'has-danger'}
                    valid={validation.UpwordState === 'has-success'}
                    placeholder="Enter new password"
                    required
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => togglePasswordVisibility('password')}
                    style={{ marginLeft: '5px' }}
                  >
                    <i className={`fas ${uiState.showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </Button>
                </div>
                
                {/* Password strength meter */}
                {formData.user_password && (
                  <div className="mt-2">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Password Strength:</small>
                      <small className={`text-${getStrengthColor()}`}>
                        <strong>{getStrengthLabel()}</strong>
                      </small>
                    </div>
                    <div className="progress" style={{ height: '5px' }}>
                      <div
                        className={`progress-bar bg-${getStrengthColor()}`}
                        role="progressbar"
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {/* Password requirements checklist */}
                <div className="mt-2 small">
                  <div className="row">
                    <div className="col-6">
                      <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasMinLength ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas ${passwordStrength.hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                        <span>8+ characters</span>
                      </div>
                      <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasUppercase ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas ${passwordStrength.hasUppercase ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                        <span>Uppercase letter</span>
                      </div>
                      <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasLowercase ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas ${passwordStrength.hasLowercase ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                        <span>Lowercase letter</span>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasNumber ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas ${passwordStrength.hasNumber ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                        <span>Number</span>
                      </div>
                      <div className={`d-flex align-items-center mb-1 ${passwordStrength.hasSpecialChar ? 'text-success' : 'text-muted'}`}>
                        <i className={`fas ${passwordStrength.hasSpecialChar ? 'fa-check-circle' : 'fa-circle'} me-1`}></i>
                        <span>Special character (#.,;~%?!@$%^&*-)</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <FormFeedback invalid>
                  The password must be at least 8 characters long and contain uppercase, lowercase, numbers, and special characters.
                </FormFeedback>
              </Col>
            </Row>
          </FormGroup>
          
          {/* Confirm Password */}
          <FormGroup>
            <Row>
              <Col xs={12} md={3}>
                <Label for="user_password_confirm" className="font-weight-bold">Confirm Password</Label>
              </Col>
              <Col xs={12} md={9}>
                <div className="d-flex">
                  <Input
                    type={uiState.showConfirmPassword ? "text" : "password"}
                    id="user_password_confirm"
                    name="user_password_confirm"
                    value={formData.user_password_confirm}
                    onChange={handleInputChange}
                    invalid={validation.UpwordConfirmState === 'has-danger'}
                    valid={validation.UpwordConfirmState === 'has-success'}
                    placeholder="Re-enter password"
                    required
                    style={{ flex: 1 }}
                  />
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => togglePasswordVisibility('confirm')}
                    style={{ marginLeft: '5px' }}
                  >
                    <i className={`fas ${uiState.showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </Button>
                </div>
                
                {validation.confirmTouched && !validation.passwordMatch && (
                  <div className="text-danger small mt-1">
                    <i className="fas fa-times-circle me-1"></i> Passwords do not match
                  </div>
                )}
                
                {validation.passwordMatch && formData.user_password_confirm && (
                  <div className="text-success small mt-1">
                    <i className="fas fa-check-circle me-1"></i> Passwords match!
                  </div>
                )}
                
                <FormFeedback invalid>
                  Password Mismatched. Retry...
                </FormFeedback>
              </Col>
            </Row>
          </FormGroup>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            type="submit" 
            color="success"
            disabled={uiState.is_loading || !validation.passwordValid || !validation.passwordMatch}
          >
            {uiState.is_loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
          <Button 
            type="button" 
            color="danger"
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                user_password: '',
                user_password_confirm: ''
              }));
              setValidation(prev => ({
                ...prev,
                passwordValid: false,
                passwordMatch: false,
                passwordTouched: false,
                confirmTouched: false,
                UpwordState: null,
                UpwordConfirmState: null
              }));
            }}
          >
            Clear
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default PasswordResetModal;